import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useBlocker } from "react-router-dom";
import { ArrowLeft, ExternalLink, Check, AlertCircle } from "lucide-react";
import { Typography } from "../components/atoms/Typography";
import { Button } from "../components/atoms/Button";
import { ClauseCard } from "../components/organisms/ClauseCard";
import { SectionSidebar } from "../components/organisms/SectionSidebar";
import {
  SectionVersionHistory,
  extractContentText,
  type CompareSelection,
} from "../components/organisms/SectionVersionHistory";
import { SectionDiffModal, type DiffSide } from "../components/organisms/SectionDiffModal";
import {
  fetchContractTemplate,
  fetchSectionIndex,
  fetchSectionDetail,
  approveSection,
  excludeSectionFromTemplate,
  activateVersion,
  deactivateVersion,
  fetchApprovedVersionDetail,
  type SectionIndexEntry,
  type ApprovedSectionData,
} from "../services/contractTemplateService";
import { useBusinessUnits } from "../hooks/useBusinessUnits";
import { useApprovedVersions } from "../hooks/useApprovedVersions";
import {
  parseContentRiskLevel,
  type ContractTemplate,
  type ContentItem,
  type ContentRiskLevel,
  type Section,
} from "../types/contractTemplate";
import { contentHasSomeText, contentItemsToApiPayload } from "../utils/sectionPayload";
import {
  ALL_BUSINESS_UNITS_SENTINEL,
  resolveBusinessUnitsForApi,
} from "../utils/businessUnitApiCodes";
import { useAuthSession } from "../contexts/AuthSessionContext";

const contentToText = (content: ContentItem[]): string => content.map((c) => c.text).join("\n\n");

const normalizeToBuLabels = (raw: string[], allUnits: readonly string[]): string[] => {
  const normalized = raw.map((bu) => bu.trim().toLowerCase());
  const hasAllBus =
    normalized.includes("all bus") ||
    normalized.includes("all bus.") ||
    normalized.includes("all buss");
  if (!raw.length) return [];
  if (hasAllBus) return allUnits.filter((label) => label !== "All BUs");
  const fromPayload = allUnits.filter((label) => normalized.includes(label.toLowerCase()));
  return fromPayload.length > 0 ? [...fromPayload] : ["On Call"];
};

export const ContractTemplateDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { adminRoles, effectiveAccess } = useAuthSession();
  const { options: businessUnitOptions } = useBusinessUnits();
  const businessUnitNameList = useMemo(
    () => businessUnitOptions.map((o) => o.name),
    [businessUnitOptions],
  );

  /** BU chip defaults when the section has no resolvable BU labels (e.g. extracted row with no scope). */
  const defaultBuSelection = useMemo((): string[] => {
    const real = businessUnitOptions.filter((o) => o.code !== ALL_BUSINESS_UNITS_SENTINEL.code);
    const onCallOption =
      real.find((o) => o.name === "On Call") ?? real.find((o) => o.code.toUpperCase() === "ONCALL");
    const onCallLabels = onCallOption?.name ? [onCallOption.name] : [];

    const rolesUpper = adminRoles.map((r) => r.trim().toUpperCase());
    if (rolesUpper.includes("SUPER_ADMIN")) {
      return onCallLabels;
    }

    const codes = effectiveAccess?.business_unit_codes ?? [];
    const fromCodes = codes
      .map((code) => real.find((o) => o.code.toUpperCase() === code.trim().toUpperCase())?.name)
      .filter((n): n is string => Boolean(n));
    if (fromCodes.length > 0) {
      return fromCodes;
    }
    return onCallLabels;
  }, [adminRoles, businessUnitOptions, effectiveAccess?.business_unit_codes]);

  // Contract header
  const [template, setTemplate] = useState<ContractTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Section index (sidebar)
  const [sectionIndex, setSectionIndex] = useState<SectionIndexEntry[]>([]);
  const [sectionTotal, setSectionTotal] = useState(0);
  const [sidebarPage, setSidebarPage] = useState(1);
  const [sidebarPerPage, setSidebarPerPage] = useState(10);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [isSidebarLoading, setIsSidebarLoading] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Active section detail
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<Section | null>(null);
  const [isSectionLoading, setIsSectionLoading] = useState(false);

  // Version history
  const {
    versions,
    total: versionsTotal,
    isLoading: isVersionsLoading,
    reload: reloadVersions,
  } = useApprovedVersions(
    id,
    selectedSectionId,
    activeSection?.sectionName ?? null,
    template?.contractType ?? null,
  );

  // Active approved version detail (eagerly fetched when versions load)
  const [activeApprovedDetail, setActiveApprovedDetail] = useState<ApprovedSectionData | null>(
    null,
  );
  const [isApprovedDetailLoading, setIsApprovedDetailLoading] = useState(false);

  // Eagerly fetch the active approved version's full payload so ClauseCard can display it.
  useEffect(() => {
    const active = versions.find((v) => v.is_active);
    if (!active || !id || !selectedSectionId) {
      setActiveApprovedDetail(null);
      return;
    }
    const fetchContractId = active.contract_id || id;
    const fetchSectionId = active.source_section_id || selectedSectionId;
    let cancelled = false;
    setIsApprovedDetailLoading(true);
    fetchApprovedVersionDetail(fetchContractId, fetchSectionId, active.id)
      .then((detail) => {
        if (!cancelled) setActiveApprovedDetail(detail);
      })
      .catch(() => {
        if (!cancelled) setActiveApprovedDetail(null);
      })
      .finally(() => {
        if (!cancelled) setIsApprovedDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, selectedSectionId, versions]);

  // Derive the "base section" for ClauseCard: active approved version if available, else extracted.
  const baseSection: Section | null = useMemo(() => {
    if (activeApprovedDetail) {
      const rawContent = (activeApprovedDetail.section_payload as Record<string, unknown>)?.content;
      const contentArr = Array.isArray(rawContent) ? rawContent : [];
      const content: ContentItem[] = contentArr
        .filter(
          (
            item: unknown,
          ): item is {
            text: string;
            type?: string;
            created_at?: string;
            text_as_html?: string | null;
          } => !!item && typeof (item as Record<string, unknown>).text === "string",
        )
        .map((item) => ({
          type: (item.type === "list_item"
            ? "list_item"
            : item.type === "table"
              ? "table"
              : item.type === "paragraph"
                ? "clause"
                : "clause") as ContentItem["type"],
          text: item.text,
          textAsHtml: typeof item.text_as_html === "string" ? item.text_as_html : undefined,
          createdAt: item.created_at ?? activeApprovedDetail.created_at,
        }));
      return {
        sectionName: activeApprovedDetail.section_name,
        businessUnit: activeApprovedDetail.business_units ?? ["All Bus"],
        activeVersion: `v${activeApprovedDetail.global_version_number}`,
        approvalStatus: activeSection?.approvalStatus,
        contentRiskLevel: parseContentRiskLevel(activeApprovedDetail.content_risk_level),
        content:
          content.length > 0
            ? content
            : [
                {
                  type: "clause" as const,
                  text: "No content.",
                  createdAt: activeApprovedDetail.created_at,
                },
              ],
      };
    }
    return activeSection;
  }, [activeApprovedDetail, activeSection]);

  /** Normalized BU labels from the loaded section, or role-based defaults when none resolve. */
  const baselineBuLabels = useMemo((): string[] => {
    const raw = baseSection?.businessUnit ?? [];
    const normalized = normalizeToBuLabels(raw, businessUnitNameList);
    return normalized.length > 0 ? normalized : defaultBuSelection;
  }, [baseSection, businessUnitNameList, defaultBuSelection]);

  // Editing state
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [draftContent, setDraftContent] = useState<ContentItem[] | null>(null);
  const [editedContent, setEditedContent] = useState<ContentItem[] | null>(null);
  const [editedMeta, setEditedMeta] = useState<{
    businessUnit: string[];
    exclude: boolean;
    contentRiskLevel?: ContentRiskLevel;
  } | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [pendingSectionId, setPendingSectionId] = useState<string | null>(null);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Diff modal state
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffLeft, setDiffLeft] = useState<DiffSide | null>(null);
  const [diffRight, setDiffRight] = useState<DiffSide | null>(null);

  const hasUnsavedEdits =
    editedContent !== null ||
    editedMeta !== null ||
    (editingSectionId !== null && draftContent !== null);

  const blocker = useBlocker(hasUnsavedEdits);
  const showConfirmPopup = blocker.state === "blocked" || pendingSectionId !== null;

  // Load contract header
  const loadTemplate = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    const data = await fetchContractTemplate(id);
    setTemplate(data);
    setIsLoading(false);
  }, [id]);

  // Load section index
  const loadSectionIndex = useCallback(
    async (page: number, perPage: number, search: string) => {
      if (!id) return;
      setIsSidebarLoading(true);
      try {
        const offset = (page - 1) * perPage;
        const result = await fetchSectionIndex(id, {
          limit: perPage,
          offset,
          q: search || undefined,
        });
        setSectionIndex(result.items);
        setSectionTotal(result.total);
        if (result.items.length > 0 && !selectedSectionId) {
          setSelectedSectionId(result.items[0].id);
        }
      } catch {
        setSectionIndex([]);
        setSectionTotal(0);
      } finally {
        setIsSidebarLoading(false);
      }
    },
    [id, selectedSectionId],
  );

  // Load single section detail
  const loadSectionDetail = useCallback(
    async (sectionId: string) => {
      if (!id) return;
      setIsSectionLoading(true);
      try {
        const section = await fetchSectionDetail(id, sectionId);
        setActiveSection(section);
      } catch {
        setActiveSection(null);
      } finally {
        setIsSectionLoading(false);
      }
    },
    [id],
  );

  // Initial load
  useEffect(() => {
    void loadTemplate();
  }, [loadTemplate]);

  useEffect(() => {
    void loadSectionIndex(sidebarPage, sidebarPerPage, sidebarSearch);
  }, [sidebarPage, sidebarPerPage]);

  // Load section detail when selection changes
  useEffect(() => {
    if (selectedSectionId) {
      setEditedContent(null);
      setEditedMeta(null);
      setEditingSectionId(null);
      setDraftContent(null);
      setSaveSuccess(null);
      setSaveError(null);
      setActiveSection(null);
      setActiveApprovedDetail(null);
      void loadSectionDetail(selectedSectionId);
    }
  }, [selectedSectionId, loadSectionDetail]);

  // Debounced search
  const handleSidebarSearch = useCallback(
    (term: string) => {
      setSidebarSearch(term);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setSidebarPage(1);
        void loadSectionIndex(1, sidebarPerPage, term);
      }, 400);
    },
    [loadSectionIndex, sidebarPerPage],
  );

  // Beforeunload guard
  useEffect(() => {
    if (!hasUnsavedEdits) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedEdits]);

  // Section selection with unsaved guard
  const handleSelectSection = useCallback(
    (sectionId: string) => {
      if (sectionId === selectedSectionId) return;
      if (hasUnsavedEdits) {
        setPendingSectionId(sectionId);
      } else {
        setSelectedSectionId(sectionId);
      }
    },
    [selectedSectionId, hasUnsavedEdits],
  );

  const getEffectiveContent = useCallback((): ContentItem[] => {
    if (editedContent) return editedContent;
    if (draftContent) return draftContent;
    return baseSection?.content ?? [];
  }, [editedContent, draftContent, baseSection]);

  const getEffectiveBU = useCallback((): string[] => {
    if (editedMeta) {
      return editedMeta.businessUnit;
    }
    return baselineBuLabels;
  }, [editedMeta, baselineBuLabels]);
  const expandBusinessUnitsForApi = useCallback(
    (units: string[]): string[] => {
      const resolved = resolveBusinessUnitsForApi(units, businessUnitOptions);
      if (resolved.length === 0) {
        const onCall = businessUnitOptions.find((o) => o.name === "On Call");
        return onCall ? [onCall.code] : ["ONCALL"];
      }
      return resolved;
    },
    [businessUnitOptions],
  );

  const getEffectiveExclude = useCallback((): boolean => {
    return editedMeta?.exclude ?? false;
  }, [editedMeta]);

  const getEffectiveRiskLevel = useCallback((): ContentRiskLevel => {
    return editedMeta?.contentRiskLevel ?? baseSection?.contentRiskLevel ?? "BusinessDecision";
  }, [editedMeta, baseSection]);

  // Editing handlers
  const handleEditStart = useCallback(() => {
    if (!baseSection) return;
    setEditingSectionId(selectedSectionId);
    setDraftContent(getEffectiveContent());
    setValidationError(null);
  }, [selectedSectionId, baseSection, getEffectiveContent]);

  const handleDraftChange = useCallback((content: ContentItem[]) => {
    setDraftContent(content);
    setValidationError(null);
  }, []);

  const handleEditSave = useCallback(() => {
    if (!editingSectionId || !baseSection) return;
    const content = draftContent ?? getEffectiveContent();
    if (!contentHasSomeText(content)) {
      setValidationError("Clause text cannot be empty.");
      return;
    }
    setValidationError(null);
    setEditedContent(content.map((c) => ({ ...c })));
    setEditingSectionId(null);
    setDraftContent(null);
  }, [editingSectionId, draftContent, baseSection, getEffectiveContent]);

  const handleEditCancel = useCallback(() => {
    setEditingSectionId(null);
    setDraftContent(null);
    setValidationError(null);
  }, []);

  const handleBUsChange = useCallback((bu: string[]) => {
    setEditedMeta((prev) => ({
      businessUnit: bu,
      exclude: prev?.exclude ?? false,
      contentRiskLevel: prev?.contentRiskLevel,
    }));
  }, []);

  const handleExcludeChange = useCallback(
    (exclude: boolean) => {
      setEditedMeta((prev) => ({
        businessUnit: prev?.businessUnit ?? baselineBuLabels,
        exclude,
        contentRiskLevel: prev?.contentRiskLevel,
      }));
    },
    [baselineBuLabels],
  );

  const handleContentRiskLevelChange = useCallback(
    (level: ContentRiskLevel) => {
      setEditedMeta((prev) => ({
        businessUnit: prev?.businessUnit ?? baselineBuLabels,
        exclude: prev?.exclude ?? false,
        contentRiskLevel: level,
      }));
    },
    [baselineBuLabels],
  );

  const handleSectionSave = useCallback(async () => {
    if (!id || !selectedSectionId || !baseSection) return;
    setSaveSuccess(null);
    setSaveError(null);

    if (getEffectiveExclude()) {
      const confirmed = window.confirm(
        "Exclude this section from the template and knowledge base? All approved versions for this section will be deactivated, " +
          "S3/KB files will be removed when no other active clause in the same group needs them. Unsaved clause edits will not be saved.",
      );
      if (!confirmed) return;
      setIsSaving(true);
      try {
        await excludeSectionFromTemplate(id, selectedSectionId);
        setEditedContent(null);
        setEditedMeta(null);
        setEditingSectionId(null);
        setDraftContent(null);
        setSaveSuccess(
          "Section excluded from template. Run a knowledge base sync to refresh vectors.",
        );
        await loadSectionDetail(selectedSectionId);
        reloadVersions();
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to exclude section. Please try again.";
        setSaveError(message);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const content = getEffectiveContent();
    if (!contentHasSomeText(content)) {
      setValidationError("Clause text cannot be empty.");
      return;
    }
    setValidationError(null);
    setIsSaving(true);
    try {
      const businessUnits = getEffectiveBU();
      const apiBusinessUnits = expandBusinessUnitsForApi(businessUnits);
      await approveSection(id, selectedSectionId, {
        business_units: apiBusinessUnits,
        content_risk_level: getEffectiveRiskLevel(),
        section_payload: {
          section_name: baseSection.sectionName ?? "Untitled",
          content: contentItemsToApiPayload(content),
        },
      });
      setEditedContent(null);
      setEditedMeta(null);
      setEditingSectionId(null);
      setDraftContent(null);
      setSaveSuccess("Section approved successfully.");
      await loadSectionDetail(selectedSectionId);
      reloadVersions();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to save section. Please try again.";
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  }, [
    id,
    selectedSectionId,
    baseSection,
    getEffectiveContent,
    getEffectiveBU,
    getEffectiveExclude,
    getEffectiveRiskLevel,
    loadSectionDetail,
    reloadVersions,
  ]);

  const handleActivateVersion = useCallback(
    async (versionId: string, scope?: { contractId: string; sectionId: string }) => {
      const targetContractId = scope?.contractId ?? id;
      const targetSectionId = scope?.sectionId ?? selectedSectionId;
      if (!targetContractId || !targetSectionId) return;
      setSaveError(null);
      setSaveSuccess(null);
      try {
        await activateVersion(targetContractId, targetSectionId, versionId);
        setSaveSuccess("Version activated successfully.");
        if (selectedSectionId) {
          await loadSectionDetail(selectedSectionId);
        }
        reloadVersions();
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to activate version. Please try again.";
        setSaveError(message);
      }
    },
    [id, selectedSectionId, loadSectionDetail, reloadVersions],
  );

  const handleDeactivateVersion = useCallback(
    async (versionId: string, scope?: { contractId: string; sectionId: string }) => {
      const targetContractId = scope?.contractId ?? id;
      const targetSectionId = scope?.sectionId ?? selectedSectionId;
      if (!targetContractId || !targetSectionId) return;
      setSaveError(null);
      setSaveSuccess(null);
      try {
        await deactivateVersion(targetContractId, targetSectionId, versionId);
        setSaveSuccess("Version deactivated successfully.");
        if (selectedSectionId) {
          await loadSectionDetail(selectedSectionId);
        }
        reloadVersions();
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to deactivate version. Please try again.";
        setSaveError(message);
      }
    },
    [id, selectedSectionId, loadSectionDetail, reloadVersions],
  );

  const handleActivateExtracted = useCallback(async () => {
    if (!id || !selectedSectionId || !activeSection) return;
    setSaveError(null);
    setSaveSuccess(null);
    try {
      const apiBusinessUnits = expandBusinessUnitsForApi(activeSection.businessUnit);
      await approveSection(id, selectedSectionId, {
        business_units: apiBusinessUnits,
        content_risk_level: getEffectiveRiskLevel(),
        section_payload: {
          section_name: activeSection.sectionName ?? "Untitled",
          content: contentItemsToApiPayload(activeSection.content),
        },
      });
      setSaveSuccess("Original extracted section activated as new version.");
      await loadSectionDetail(selectedSectionId);
      reloadVersions();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to activate extracted section.";
      setSaveError(message);
    }
  }, [
    id,
    selectedSectionId,
    activeSection,
    expandBusinessUnitsForApi,
    getEffectiveRiskLevel,
    loadSectionDetail,
    reloadVersions,
  ]);

  const handleCompare = useCallback(
    async (left: CompareSelection, right: CompareSelection) => {
      if (!id || !selectedSectionId) return;

      const CURRENT_KEY = "__current__";
      const EXTRACTED_KEY = "__extracted__";

      const getContent = async (sel: CompareSelection): Promise<DiffSide> => {
        if (sel.id === CURRENT_KEY) {
          const text = contentToText(getEffectiveContent());
          return { label: sel.label, sublabel: sel.sublabel, content: text };
        }
        if (sel.id === EXTRACTED_KEY) {
          const text = contentToText(activeSection?.content ?? []);
          return { label: sel.label, sublabel: sel.sublabel, content: text, isExtracted: true };
        }
        const ver = versions.find((v) => v.id === sel.id);
        const detailContractId = ver?.contract_id ?? id;
        const detailSectionId = ver?.source_section_id ?? selectedSectionId;
        if (!detailContractId || !detailSectionId) {
          throw new Error("Missing version scope");
        }
        const detail = await fetchApprovedVersionDetail(detailContractId, detailSectionId, sel.id);
        const text = extractContentText(detail.section_payload);
        return {
          label: sel.label,
          sublabel: sel.sublabel,
          content: text,
          versionId: sel.id,
          contractId: ver?.contract_id ?? id,
          sectionId: ver?.source_section_id ?? selectedSectionId,
          isActive: ver?.is_active ?? false,
        };
      };

      try {
        const [l, r] = await Promise.all([getContent(left), getContent(right)]);
        setDiffLeft(l);
        setDiffRight(r);
        setDiffOpen(true);
      } catch {
        setSaveError("Failed to load version content for comparison.");
      }
    },
    [id, selectedSectionId, getEffectiveContent, activeSection, versions],
  );

  const handleSectionCancel = useCallback(() => {
    setEditedContent(null);
    setEditedMeta(null);
    setEditingSectionId(null);
    setDraftContent(null);
    setValidationError(null);
  }, []);

  const clearEdits = useCallback(() => {
    setEditingSectionId(null);
    setDraftContent(null);
    setEditedContent(null);
    setEditedMeta(null);
  }, []);

  const handleConfirmDiscard = useCallback(() => {
    if (pendingSectionId !== null) {
      clearEdits();
      setSelectedSectionId(pendingSectionId);
      setPendingSectionId(null);
    }
    if (blocker.state === "blocked") {
      blocker.proceed?.();
      clearEdits();
    }
  }, [pendingSectionId, blocker, clearEdits]);

  const handleCancelDiscard = useCallback(() => {
    setPendingSectionId(null);
    blocker.reset?.();
  }, [blocker]);

  if (isLoading) {
    return (
      <section>
        <Typography variant="muted">Loading contract template…</Typography>
      </section>
    );
  }

  if (!template) {
    return (
      <section>
        <button
          type="button"
          onClick={() => navigate("/contracts")}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 transition-colors hover:text-primary-600"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Contracts
        </button>
        <Typography variant="muted">Contract template not found.</Typography>
      </section>
    );
  }

  const effectiveSection: Section | null = baseSection
    ? {
        ...baseSection,
        content: getEffectiveContent(),
        businessUnit: getEffectiveBU(),
        contentRiskLevel: getEffectiveRiskLevel(),
      }
    : null;
  const activeVersionForSelectedSection = versions.find(
    (v) =>
      v.is_active &&
      (!id || !v.contract_id || v.contract_id === id) &&
      (!selectedSectionId || !v.source_section_id || v.source_section_id === selectedSectionId),
  );
  const activeVersionLabel = activeApprovedDetail
    ? `v${activeApprovedDetail.global_version_number}`
    : activeVersionForSelectedSection
      ? `v${activeVersionForSelectedSection.global_version_number}`
      : null;

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/contracts")}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            aria-label="Back to Contracts"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <Typography as="h1" variant="h1">
              Contract Template Detail
            </Typography>
            <Typography variant="muted">Review and configure contract clauses</Typography>
          </div>
        </div>
      </div>

      {/* Two-column layout: sidebar + content */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <SectionSidebar
          sections={sectionIndex}
          selectedSectionId={selectedSectionId}
          onSelectSection={handleSelectSection}
          totalCount={sectionTotal}
          isLoading={isSidebarLoading}
          page={sidebarPage}
          perPage={sidebarPerPage}
          onChangePage={setSidebarPage}
          onChangeRowsPerPage={(pp) => {
            setSidebarPerPage(pp);
            setSidebarPage(1);
          }}
          searchTerm={sidebarSearch}
          onSearchChange={handleSidebarSearch}
        />
        <div className="flex-1 overflow-auto p-6">
          {isSectionLoading || isApprovedDetailLoading ? (
            <Typography variant="muted">Loading section…</Typography>
          ) : effectiveSection ? (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {activeVersionLabel ? (
                  <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200">
                    Active - {activeVersionLabel}
                  </span>
                ) : (
                  <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                    No active version
                  </span>
                )}
                {versions.length > 0 &&
                  !activeVersionForSelectedSection &&
                  activeSection?.approvalStatus === "Pending" && (
                    <span
                      className={
                        activeVersionLabel
                          ? "rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-800"
                          : "rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800"
                      }
                    >
                      {activeVersionLabel
                        ? "Active in knowledge base (another contract)"
                        : "No active approval — pending re-approval"}
                    </span>
                  )}
              </div>
              <ClauseCard
                section={effectiveSection}
                sectionIndex={0}
                isEditing={editingSectionId === selectedSectionId}
                draftContent={editingSectionId === selectedSectionId ? draftContent : null}
                selectedBUs={normalizeToBuLabels(getEffectiveBU(), businessUnitNameList)}
                excluded={getEffectiveExclude()}
                onEditStart={handleEditStart}
                onDraftChange={handleDraftChange}
                onEditSave={handleEditSave}
                onEditCancel={handleEditCancel}
                onBUsChange={handleBUsChange}
                onExcludeChange={handleExcludeChange}
                contentRiskLevel={getEffectiveRiskLevel()}
                onContentRiskLevelChange={handleContentRiskLevelChange}
                validationError={editingSectionId === selectedSectionId ? validationError : null}
                hasUnsavedEdits={hasUnsavedEdits}
                sectionSaveEnabled={hasUnsavedEdits || !activeVersionForSelectedSection}
                isSaving={isSaving}
                onSectionSave={handleSectionSave}
                onSectionCancel={handleSectionCancel}
              />
              {saveSuccess && (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  <Check className="h-4 w-4 shrink-0" />
                  {saveSuccess}
                </div>
              )}
              {saveError && (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {saveError}
                </div>
              )}
              <SectionVersionHistory
                contractId={id!}
                sectionId={selectedSectionId!}
                currentSection={effectiveSection}
                extractedSection={activeSection}
                hasUnsavedEdits={hasUnsavedEdits}
                versions={versions}
                total={versionsTotal}
                isLoading={isVersionsLoading}
                onCompare={handleCompare}
                onActivate={handleActivateVersion}
                onDeactivate={handleDeactivateVersion}
                onActivateExtracted={handleActivateExtracted}
              />
            </>
          ) : sectionTotal === 0 ? (
            <Typography variant="muted">
              This contract has no sections yet. Sections will appear after processing completes.
            </Typography>
          ) : (
            <Typography variant="muted">Select a section from the sidebar.</Typography>
          )}
        </div>
      </div>

      {/* Confirmation popup */}
      {showConfirmPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="unsaved-dialog-title"
        >
          <div className="mx-4 max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 id="unsaved-dialog-title" className="mb-4 text-lg font-semibold text-gray-900">
              You have unsaved changes. Save or discard before leaving?
            </h2>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelDiscard}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDiscard}
                className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Diff modal */}
      {diffLeft && diffRight && (
        <SectionDiffModal
          open={diffOpen}
          onClose={() => setDiffOpen(false)}
          left={diffLeft}
          right={diffRight}
          onActivate={handleActivateVersion}
          onDeactivate={handleDeactivateVersion}
          onActivateExtracted={handleActivateExtracted}
        />
      )}
    </section>
  );
};
